import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, json } from '../api/client'
import type { CreatedWebhook, WebhookDelivery, WebhookEndpoint, WebhookEventType } from '../api/types'

const webhooksKey = (ws: string) => ['workspace', ws, 'webhooks'] as const

export function useWebhookEvents() {
  return useQuery({ queryKey: ['webhook-events'], queryFn: () => api<WebhookEventType[]>('/webhook-events'), staleTime: Infinity })
}

export function useWebhooks(ws: string) {
  return useQuery({ queryKey: webhooksKey(ws), queryFn: () => api<WebhookEndpoint[]>(`/workspaces/${ws}/webhooks`) })
}

/** An endpoint's recent deliveries, polled while one is waiting or being sent. */
export function useWebhookDeliveries(ws: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...webhooksKey(ws), id, 'deliveries'],
    queryFn: () => api<WebhookDelivery[]>(`/workspaces/${ws}/webhooks/${id}/deliveries`),
    enabled,
    refetchInterval: (query) => (query.state.data?.some((d) => d.status === 'ready' || d.status === 'leased') ? 3_000 : false),
  })
}

/** A change to the workspace's endpoints; refreshes the list and every delivery log. */
function useWebhookChange<Input, Output>(ws: string, change: (input: Input) => Promise<Output>) {
  const queryClient = useQueryClient()
  return useMutation({ mutationFn: change, onSuccess: () => queryClient.invalidateQueries({ queryKey: webhooksKey(ws) }) })
}

export function useCreateWebhook(ws: string) {
  return useWebhookChange(ws, (input: { url: string; events: string[]; description: string }) =>
    api<CreatedWebhook>(`/workspaces/${ws}/webhooks`, { method: 'POST', body: json(input) }),
  )
}

export function useUpdateWebhook(ws: string) {
  return useWebhookChange(ws, ({ id, ...change }: { id: string; enabled?: boolean; events?: string[]; url?: string }) =>
    api<WebhookEndpoint>(`/workspaces/${ws}/webhooks/${id}`, { method: 'PATCH', body: json(change) }),
  )
}

export function useDeleteWebhook(ws: string) {
  return useWebhookChange(ws, (id: string) => api<void>(`/workspaces/${ws}/webhooks/${id}`, { method: 'DELETE' }))
}

export function useRotateWebhookSecret(ws: string) {
  return useWebhookChange(ws, (id: string) => api<{ secret: string }>(`/workspaces/${ws}/webhooks/${id}/secret`, { method: 'POST' }))
}

export function useTestWebhook(ws: string) {
  return useWebhookChange(ws, (id: string) => api<{ delivery_id: string }>(`/workspaces/${ws}/webhooks/${id}/test`, { method: 'POST' }))
}
